import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import EmployeeDashboardLayout from './EmployeeDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
    isEmployeeDeliveryAgent,
    resolveDeliveryAgentOrderSourceFromState,
} from './deliveryAgentSession';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
    fetchOrderById,
    fetchRestaurantOrders,
    fetchRestaurantOrderHistory,
    fetchRestaurantOrderById,
    formatSchoolAddressFromParts,
    buildSchoolMapUrl,
} from './orderApi';
import EventLocationMapLink from './EventLocationMapLink';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import { getOrderChannel, resolveRestaurantOrderChannel } from './menuChannels';

import {
    formatMobileDisplayOrFallback,
    formatOrderDateTime,
    formatOrderMobileDisplay,
    formatPaymentMethodDisplay,
    formatUserFullName,
    formatDisplayOrderNo,
    isCashPaymentMethod,
    resolveOrderCreatedById,
    resolveOrderPaymentMethod,
    toDisplayLabel,
} from './formatDisplay';
import { fetchRestaurantUserById } from './inventoryApi';
import {
    formatOrderBillingLabel,
    formatOrderMoney,
    isSubscriptionCreditOrder,
    resolveOrderCreditsConsumed,
    resolveOrderPlatformFee,
    resolveOrderSubscriptionId,
    resolveRestaurantSettlementAmount,
} from './orderBillingUtils';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';

function buildOrdersBackPath(location, orderDetails) {
    const orderType = resolveRestaurantOrderChannel(orderDetails, location.state);
    const params = new URLSearchParams();
    params.set('tab', 'orders');
    params.set('orderType', orderType === 'online' ? 'myeats' : orderType);

    if (location.state?.fromOrderHistory) {
        if (orderType === 'catering') {
            params.set('cateringView', 'orderHistory');
        } else if (orderType === 'offline') {
            params.set('offlineView', 'orderHistory');
        } else if (
            orderType === 'myeats' ||
            orderType === 'jreats' ||
            orderType === 'online'
        ) {
            params.set('onlineView', 'orderHistory');
        }
    }

    return `/business/admin?${params.toString()}`;
}


function orderStatusBadgeClass(status) {
    const s = String(status || '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
    if (s === 'CANCELLED' || s === 'CAN') return 'inactive';
    if (['DELIVERED', 'COMPLETED', 'CMP', 'PICKEDUP', 'PU'].includes(s)) return 'active';
    return 'pending';
}

const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
};

function isSuccessfulPaymentStatus(status) {
    const norm = String(status ?? '').trim().toUpperCase();
    return (
        norm === 'SUCCESS' ||
        norm === 'COMPLETED' ||
        norm === 'COMPLETE' ||
        norm === 'PAID' ||
        norm === 'SUCCEEDED'
    );
}

function orderLooksLikeCatering(order) {
    const orderType = String(order?.orderType ?? order?.order_type ?? order?.Order_Type ?? '')
        .trim()
        .toLowerCase();
    return orderType === 'catering';
}

function isCateringOrderRecord(order, fromOrderView) {
    if (fromOrderView === 'catering') return true;
    if (fromOrderView === 'online' || fromOrderView === 'offline' || fromOrderView === 'partyHall') {
        return false;
    }
    return orderLooksLikeCatering(order);
}

const DetailField = ({ label, value, wide = false, badge = false, children }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
        <span className="restaurant-detail-label">{label}</span>
        {children ??
            (badge ? (
                <span
                    className={`restaurant-detail-badge restaurant-detail-badge-${orderStatusBadgeClass(value)}`}
                >
                    {displayValue(value)}
                </span>
            ) : (
                <span className="restaurant-detail-value">{displayValue(value)}</span>
            ))}
    </div>
);

function toMoneyNumber(value) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
}

function resolveOrderTaxes(order) {
    return {
        platformFee: toMoneyNumber(
            order?.platformFee ?? order?.platform_fee ?? order?.Platform_Fee
        ),
        gst: toMoneyNumber(order?.gst ?? order?.GST ?? order?.Gst),
        deliveryFee: toMoneyNumber(
            order?.deliveryFee ?? order?.delivery_fee ?? order?.DeliveryFee ?? order?.Delivery_Fee
        ),
        convienceFee: toMoneyNumber(
            order?.convienceFee ??
                order?.convience_fee ??
                order?.ConvenienceFee ??
                order?.Convience_Fee
        ),
    };
}

const OrderDetails = () => {
    const { orderId, restaurantId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
        useRestaurantEnabledServices();
    const [orderDetails, setOrderDetails] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [paymentDetails, setPaymentDetails] = useState({});
    const [createdByLabel, setCreatedByLabel] = useState('—');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            setLoading(true);
            setCreatedByLabel('—');
            try {
                const fromOrderView = location.state?.fromOrderView;
                const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
                let order = localOrders.find((o) => {
                    const idMatch =
                        o.orderId === Number(orderId) ||
                        o.orderId === String(orderId) ||
                        o.orderNumber === Number(orderId) ||
                        o.orderNumber === String(orderId);
                    if (!idMatch) return false;
                    // Don't let a local catering row hijack MyEats / dine-in order details.
                    if (
                        (fromOrderView === 'online' || fromOrderView === 'offline') &&
                        orderLooksLikeCatering(o)
                    ) {
                        return false;
                    }
                    if (fromOrderView === 'catering' && !orderLooksLikeCatering(o)) {
                        return false;
                    }
                    return true;
                });

                if (!order) {
                    const token = localStorage.getItem('Token');
                    const headers = token ? { Authorization: `Bearer ${token}` } : {};
                    if (fromOrderView === 'catering') {
                        try {
                            const cateringRes = await axiosRestaurantInstance.get(
                                RESTAURANT_API_PATHS.cateringOrderById(orderId),
                                { headers }
                            );
                            const co = cateringRes?.data;
                            if (co && (co.cateringOrderId != null || co.orderId != null)) {
                                const lineItems = (co.lineItems || []).map(li => ({
                                    foodItemName: li.itemName ?? li.Item_Name ?? li.foodItemName,
                                    foodQuantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
                                    quantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
                                    price: parseFloat(li.price || 0),
                                }));
                                order = {
                                    orderId: co.cateringOrderId ?? co.orderId,
                                    orderNumber: co.cateringOrderId ?? co.orderId,
                                    customerName: co.customerName ?? co.Customer_Name ?? 'N/A',
                                    mobile: co.customerContact ?? co.customerMobile ?? '',
                                    customerMobile: co.customerContact ?? co.customerMobile ?? '',
                                    totalPrice: parseFloat(co.totalPrice || 0),
                                    orderStatus: (co.orderStatus ?? co.Order_Status ?? 'NEW').toString().toUpperCase(),
                                    createdDate: co.createdDate ?? co.createdAt,
                                    eventDate: co.eventDate ?? co.Event_Date,
                                    eventLocation: co.deliveryAddress ?? co.eventLocation ?? co.Event_Location,
                                    orderLineItems: lineItems,
                                    OrderLineItems: lineItems,
                                    orderType: 'catering',
                                    restaurantId: co.restaurantId ?? co.Restaurant_Id,
                                };
                            }
                        } catch (cateringErr) {
                            console.log('Catering order fetch failed:', cateringErr?.response?.status);
                        }
                    } else {
                        order = await fetchOrderById(orderId);
                        if (!order && restaurantId) {
                            try {
                                const matchesOrderId = (o) =>
                                    Number(o.orderId) === Number(orderId) ||
                                    String(o.orderId) === String(orderId);

                                if (fromOrderView === 'offline') {
                                    order = await fetchRestaurantOrderById(restaurantId, orderId);
                                }

                                if (!order) {
                                    const historyOrders = await fetchRestaurantOrderHistory(restaurantId);
                                    order = historyOrders.find(matchesOrderId);
                                }

                                if (!order) {
                                    const activeOrders = await fetchRestaurantOrders(restaurantId);
                                    order = activeOrders.find(matchesOrderId);
                                }
                            } catch (apiError) {
                                console.log('Restaurant orders list fallback failed:', apiError);
                            }
                        }
                    }
                }

                if (order) {
                    // localStorage POS rows often omit createdById / nested payment — enrich from API.
                    if (
                        fromOrderView !== 'catering' &&
                        !orderLooksLikeCatering(order) &&
                        resolveOrderCreatedById(order) == null
                    ) {
                        try {
                            const apiOrder = await fetchOrderById(orderId);
                            if (apiOrder) {
                                order = {
                                    ...apiOrder,
                                    ...order,
                                    createdById:
                                        resolveOrderCreatedById(order) ??
                                        resolveOrderCreatedById(apiOrder),
                                    created_by_id:
                                        order.created_by_id ??
                                        apiOrder.created_by_id ??
                                        apiOrder.createdById,
                                    payment:
                                        order.payment?.status ||
                                        order.payment?.Status ||
                                        order.payment?.payment_method ||
                                        order.payment?.paymentMethod
                                            ? order.payment
                                            : apiOrder.payment ?? order.payment,
                                    paymentMethod:
                                        resolveOrderPaymentMethod(order) ??
                                        resolveOrderPaymentMethod(apiOrder),
                                    paymentStatus:
                                        order.paymentStatus ??
                                        apiOrder.paymentStatus ??
                                        null,
                                };
                            }
                        } catch (enrichErr) {
                            console.log('Order details enrich from API failed:', enrichErr);
                        }
                    }

                    const paymentMethod = resolveOrderPaymentMethod(order);
                    const cash = isCashPaymentMethod(paymentMethod);
                    const txnId =
                        order.transactionId ??
                        order.payment?.transactionId ??
                        order.payment?.transaction_id ??
                        null;
                    const bankRrn =
                        order.bankRRN ??
                        order.payment?.bankRRN ??
                        order.payment?.bank_rrn ??
                        null;
                    setOrderDetails({
                        ...order,
                        paymentMethod: paymentMethod ?? order.paymentMethod ?? null,
                    });
                    setOrderItems(order.orderLineItems || order.OrderLineItems || order.lineItems || []);
                    setPaymentDetails({
                        paymentMethod: paymentMethod ?? null,
                        transactionId: cash ? null : txnId,
                        status:
                            order.paymentStatus ??
                            order.payment?.status ??
                            order.payment?.Status ??
                            (paymentMethod ? 'SUCCESS' : 'N/A'),
                        amount:
                            order.payment?.amount ??
                            order.payment?.Amount ??
                            order.totalPrice ??
                            'N/A',
                        bankRRN: cash ? null : bankRrn,
                        customerDetails:
                            order.payment?.customerDetails ??
                            order.payment?.customer_details ??
                            order.customerName ??
                            'N/A',
                    });

                    const createdById = resolveOrderCreatedById(order);
                    if (createdById != null) {
                        let label = `User #${createdById}`;
                        try {
                            const sessionUser = JSON.parse(localStorage.getItem('user') || 'null');
                            const sessionId =
                                sessionUser?.userId ?? sessionUser?.id ?? sessionUser?.User_Id;
                            if (
                                sessionId != null &&
                                String(sessionId) === String(createdById)
                            ) {
                                label = formatUserFullName(sessionUser, label);
                            } else {
                                const userRow = await fetchRestaurantUserById(createdById);
                                if (userRow) {
                                    label = formatUserFullName(userRow, label);
                                }
                            }
                        } catch {
                            /* keep User #id */
                        }
                        setCreatedByLabel(label);
                    }
                } else {
                    console.error(`Order with ID ${orderId} not found`);
                    setOrderDetails({});
                }
            } catch (error) {
                console.error('Error fetching order details:', error);
                setOrderDetails({});
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderId, restaurantId, location.state?.fromOrderView, location.state?.fromOrderHistory]);

    const isDeliveryAgent = isEmployeeDeliveryAgent();
    const deliveryOrderSource = resolveDeliveryAgentOrderSourceFromState(
        location.state,
        orderDetails ?? {}
    );
    const ordersBackPath = buildOrdersBackPath(location, orderDetails ?? {});
    const orderNumber = formatDisplayOrderNo(orderDetails, orderId);
    const orderChannel = getOrderChannel(
        resolveRestaurantOrderChannel(orderDetails ?? {}, location.state)
    ).key;
    const backPath = isDeliveryAgent ? '/business/user' : ordersBackPath;
    const handleSelectOrderChannel = (key) => {
        navigate(`/home?tab=orders&orderType=${getOrderChannel(key).key}`);
    };
    const orderLayoutProps = {
        activeTab: 'orders',
        orderChannel,
        onSelectOrderChannel: handleSelectOrderChannel,
        enabledServiceTypes,
        servicesLoading,
    };

    const wrapDetailPage = (content) => {
        if (isDeliveryAgent) {
            return (
                <EmployeeDashboardLayout deliveryOrderSource={deliveryOrderSource}>
                    {content}
                </EmployeeDashboardLayout>
            );
        }
        return (
            <RestaurantDashboardLayout {...orderLayoutProps}>
                {content}
            </RestaurantDashboardLayout>
        );
    };

    const showPaymentDetails =
        orderDetails &&
        isSuccessfulPaymentStatus(paymentDetails.status);

    if (loading) {
        return wrapDetailPage(
            <div className="order-detail-page">
                <div className="order-details-loading">Loading order details…</div>
            </div>
        );
    }

    if (!orderDetails || Object.keys(orderDetails).length === 0) {
        return wrapDetailPage(
            <div className="order-detail-page">
                <div className="restaurant-detail-panel">
                    <div className="order-details-empty">
                        <p>Order not found.</p>
                        <button
                            type="button"
                            className="order-details-back-button restaurant-detail-back-button"
                            onClick={() =>
                                navigate(backPath, {
                                    state: { deliveryOrderSource },
                                })
                            }
                        >
                            <FaArrowLeft /> Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        const raw = String(dateString).trim();
        const parsed = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
        if (!Number.isNaN(parsed.getTime())) {
            const day = String(parsed.getDate()).padStart(2, '0');
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const year = parsed.getFullYear();
            const hours = String(parsed.getHours()).padStart(2, '0');
            const minutes = String(parsed.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
        }
        return raw || '—';
    };

    const formatMoney = (amount) => {
        const n = parseFloat(amount);
        if (Number.isNaN(n)) return displayValue(amount);
        return `₹${n.toFixed(2)}`;
    };

    const fromOrderView = location.state?.fromOrderView;
    const orderTypeNorm = String(orderDetails.order_type ?? orderDetails.Order_Type ?? orderDetails.orderType ?? '')
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
    const orderSourceNorm = String(orderDetails.order_source ?? orderDetails.Order_Source ?? orderDetails.orderSource ?? '').toUpperCase();
    const isOnlineOrder =
        fromOrderView === 'online' ||
        orderTypeNorm === 'DELIVERY' ||
        orderTypeNorm === 'PICKUP' ||
        orderSourceNorm === 'MYEATS' ||
        orderSourceNorm === 'JREATS';
    const isJreatsOrder = orderSourceNorm === 'JREATS';
    const childId = orderDetails.childId ?? orderDetails.child_id;
    const rollNumber = orderDetails.rollNumber ?? orderDetails.roll_number;
    const organizationName = orderDetails.organizationName ?? orderDetails.organization_name;
    const schoolAddressDisplay =
        orderDetails.schoolAddressFormatted ??
        orderDetails.school_address_formatted ??
        formatSchoolAddressFromParts(
            orderDetails.schoolAddress ?? orderDetails.school_address
        );
    const schoolMapUrl = buildSchoolMapUrl(
        orderDetails.schoolAddress ?? orderDetails.school_address,
        schoolAddressDisplay
    );
    const classSectionDisplay = (() => {
        const cls = orderDetails.childClass ?? orderDetails.child_class ?? orderDetails.class;
        const section = orderDetails.section ?? orderDetails.Section;
        const hasClass = cls != null && String(cls).trim() !== '';
        const hasSection = section != null && String(section).trim() !== '';
        if (hasClass && hasSection) return `${String(cls).trim()} - ${String(section).trim()}`;
        if (hasClass) return String(cls).trim();
        if (hasSection) return String(section).trim();
        return '—';
    })();
    const hideTableNumber =
        fromOrderView === 'online' ||
        fromOrderView === 'catering' ||
        orderDetails.orderType === 'catering' ||
        orderTypeNorm === 'DELIVERY' ||
        orderTypeNorm === 'PICKUP' ||
        orderSourceNorm === 'MYEATS' ||
        orderSourceNorm === 'JREATS';
    const taxTotals = resolveOrderTaxes(orderDetails);
    const itemsSubtotal = orderItems.reduce((sum, item) => {
        const itemPrice = toMoneyNumber(item.price);
        const quantity = parseInt(item.foodQuantity || item.quantity || 1, 10) || 1;
        return sum + itemPrice * quantity;
    }, 0);
    const platformFeeAmount = taxTotals.platformFee;
    const orderGrandTotal = toMoneyNumber(orderDetails.totalPrice);
    const lineTaxesTotal =
        taxTotals.gst + taxTotals.deliveryFee + taxTotals.convienceFee;
    const explainedTotal = itemsSubtotal + lineTaxesTotal + platformFeeAmount;
    const remainder = Math.round((orderGrandTotal - explainedTotal) * 100) / 100;
    // When order GST is missing but the paid total is higher, show the gap as GST.
    const displayGst =
        taxTotals.gst > 0 ? taxTotals.gst : remainder > 0.009 ? remainder : 0;
    const billingRows = [
        { label: 'Items subtotal', value: itemsSubtotal },
        displayGst > 0 ? { label: 'GST', value: displayGst } : null,
        taxTotals.deliveryFee > 0
            ? { label: 'Delivery fee', value: taxTotals.deliveryFee }
            : null,
        taxTotals.convienceFee > 0
            ? { label: 'Convenience fee', value: taxTotals.convienceFee }
            : null,
        platformFeeAmount > 0
            ? { label: 'Platform fee', value: platformFeeAmount }
            : null,
        taxTotals.gst > 0 && remainder > 0.009
            ? { label: 'Other charges', value: remainder }
            : null,
        { label: 'Grand total', value: orderGrandTotal, emphasis: true },
    ].filter(Boolean);
    const showCatering = isCateringOrderRecord(orderDetails, fromOrderView);
    const isDeliveryOrder =
        orderTypeNorm === 'DELIVERY' ||
        (isOnlineOrder && orderTypeNorm !== 'PICKUP');
    const receiverNameDisplay =
        orderDetails.receiverName ?? orderDetails.receiver_name ?? orderDetails.Receiver_Name;
    const receiverPhoneDisplay =
        orderDetails.receiverPhone ??
        orderDetails.receiver_phone ??
        orderDetails.Receiver_Phone;
    const deliveryAgentName =
        orderDetails.deliveryContactName ??
        orderDetails.delivery_contact_name ??
        orderDetails.Delivery_Contact_Name;
    const deliveryAgentPhone =
        orderDetails.deliveryContactNumber ??
        orderDetails.delivery_contact_number ??
        orderDetails.Delivery_Contact_Number ??
        orderDetails.deliveryContactNo;
    const deliveryTimeDisplay =
        orderDetails.deliveryTime ?? orderDetails.delivery_time ?? orderDetails.Delivery_Time;

    const handlePrintReceiptCopy = () => {
        const rid =
            orderDetails.restaurantId ??
            orderDetails.Restaurant_Id ??
            restaurantId ??
            localStorage.getItem('restaurantId');
        const receiptItems = (Array.isArray(orderItems) ? orderItems : []).map((item) => ({
            foodItemName:
                item.foodItemName || item.name || item.itemName || item.Food_Item || 'N/A',
            foodQuantity: parseInt(item.foodQuantity || item.quantity || 1, 10) || 1,
            quantity: parseInt(item.foodQuantity || item.quantity || 1, 10) || 1,
            price: toMoneyNumber(item.price),
        }));
        const orderData = {
            orderId: orderDetails.orderId ?? orderDetails.Order_Id ?? orderId,
            orderNumber:
                orderDetails.displayOrderNo ??
                orderDetails.display_order_no ??
                orderDetails.orderNumber ??
                orderDetails.orderId ??
                orderDetails.Order_Id ??
                orderId,
            displayOrderNo:
                orderDetails.displayOrderNo ?? orderDetails.display_order_no ?? null,
            mobile: orderDetails.customerPhone ?? orderDetails.mobile ?? orderDetails.Mobile,
            tableNumber:
                orderDetails.tableNumber ??
                orderDetails.table_number ??
                orderDetails.TableNumber,
            paymentMethod:
                paymentDetails.paymentMethod ??
                orderDetails.paymentMethod ??
                orderDetails.Payment_Method ??
                'CASH',
            billNo:
                orderDetails.displayOrderNo ??
                orderDetails.display_order_no ??
                orderDetails.billNo ??
                orderDetails.bill_no ??
                null,
            items: receiptItems,
            subtotal: itemsSubtotal,
            gst: displayGst,
            total: orderGrandTotal,
            gstPercentage: itemsSubtotal > 0 ? (displayGst / itemsSubtotal) * 100 : 2.5,
            restaurantId: rid,
            restaurantName:
                orderDetails.restaurantName ??
                orderDetails.Restaurant_Name ??
                null,
            createdDate: orderDetails.createdDate || orderDetails.createdAt,
            transactionId: paymentDetails.transactionId ?? null,
            bankRRN: paymentDetails.bankRRN ?? null,
            orderType:
                fromOrderView === 'offline' || orderTypeNorm === 'DINEIN'
                    ? 'offline'
                    : fromOrderView === 'catering'
                      ? 'catering'
                      : 'online',
        };
        navigate('/receipt', {
            state: {
                orderData,
                returnTo: `${location.pathname}${location.search || ''}`,
                returnState: location.state || null,
            },
        });
    };

    return wrapDetailPage(
        <div className="order-detail-page">
            <div className="restaurant-detail-panel">
            <div className="restaurant-detail-card restaurant-detail-card--summary">
                <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                    <div className="inventory-item-detail-header-row order-detail-summary-header-row">
                        <h3 className="restaurant-detail-section-title">Order Summary</h3>
                        <div className="restaurant-detail-card-header-actions">
                            <button
                                type="button"
                                className="edit-button order-details-receipt-copy-btn"
                                onClick={handlePrintReceiptCopy}
                            >
                                Receipt Copy
                            </button>
                            <button
                                type="button"
                                className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                onClick={() =>
                                    navigate(backPath, {
                                        state: { deliveryOrderSource },
                                    })
                                }
                                aria-label="Back to orders"
                            >
                                <FaArrowLeft aria-hidden />
                            </button>
                        </div>
                    </div>
                </div>
                    <div className="restaurant-detail-grid">
                        <DetailField label="Order" value={orderNumber} />
                        <DetailField label="Customer name" value={orderDetails.customerName} />
                        <DetailField label="Mobile" value={formatOrderMobileDisplay(orderDetails)} />
                        <DetailField label="Order status" value={orderDetails.orderStatus} badge />
                        <DetailField
                            label="Created on"
                            value={formatOrderDateTime(
                                orderDetails.createdDate || orderDetails.createdAt
                            )}
                        />
                        <DetailField label="Created by" value={createdByLabel} />
                        <DetailField label="Total price" value={formatMoney(orderDetails.totalPrice)} />
                        {isJreatsOrder && (
                            <DetailField
                                label="Platform fee"
                                value={formatOrderMoney(resolveOrderPlatformFee(orderDetails))}
                            />
                        )}
                        {isJreatsOrder && (
                            <DetailField
                                label="Billing type"
                                value={formatOrderBillingLabel(orderDetails)}
                            />
                        )}
                        {!hideTableNumber && (
                            <DetailField
                                label="Table number"
                                value={
                                    orderDetails.tableNumber ||
                                    orderDetails.table_number ||
                                    orderDetails.TableNumber
                                }
                            />
                        )}
                        {!isOnlineOrder && (
                            <DetailField
                                label="Payment method"
                                value={formatPaymentMethodDisplay(orderDetails)}
                            />
                        )}
                    </div>
                </div>

                {isJreatsOrder && (
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">School Details</h3>
                        </div>
                        <div className="restaurant-detail-grid">
                            <DetailField label="Organization name" value={organizationName} wide />
                            <div className="restaurant-detail-field restaurant-detail-field-wide">
                                <span className="restaurant-detail-label">Organization address</span>
                                <span className="restaurant-detail-value">{displayValue(schoolAddressDisplay)}</span>
                                {schoolMapUrl && schoolAddressDisplay && schoolAddressDisplay !== '—' && (
                                    <a
                                        href={schoolMapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="order-details-map-link"
                                    >
                                        <FaExternalLinkAlt aria-hidden />
                                        View on map
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {isJreatsOrder && (
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">Student details</h3>
                        </div>
                        <div className="restaurant-detail-grid">
                            <DetailField label="Child ID" value={childId} />
                            <DetailField label="Roll no" value={rollNumber} />
                            <DetailField label="Class & section" value={classSectionDisplay} />
                        </div>
                    </div>
                )}

                {isJreatsOrder && isSubscriptionCreditOrder(orderDetails) && (
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">Subscription billing</h3>
                        </div>
                        <div className="restaurant-detail-grid">
                            <DetailField
                                label="Subscription ID"
                                value={resolveOrderSubscriptionId(orderDetails)}
                            />
                            <DetailField
                                label="Credits used"
                                value={resolveOrderCreditsConsumed(orderDetails)}
                            />
                            <DetailField
                                label="Restaurant settlement"
                                value={formatOrderMoney(
                                    resolveRestaurantSettlementAmount(orderDetails)
                                )}
                            />
                        </div>
                    </div>
                )}

                {isDeliveryOrder && (
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">Delivery</h3>
                        </div>
                        <div className="restaurant-detail-grid">
                            <DetailField label="Receiver name" value={receiverNameDisplay} />
                            <DetailField label="Receiver phone" value={formatMobileDisplayOrFallback(receiverPhoneDisplay)} />
                            <DetailField label="Delivery agent name" value={deliveryAgentName} />
                            <DetailField label="Delivery agent phone" value={formatMobileDisplayOrFallback(deliveryAgentPhone)} />
                            <DetailField
                                label="Delivery time"
                                value={formatDateTime(deliveryTimeDisplay)}
                            />
                        </div>
                    </div>
                )}

                {showCatering && (
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">Catering</h3>
                        </div>
                        <div className="restaurant-detail-grid">
                            <DetailField
                                label="Event date"
                                value={formatDate(orderDetails.eventDate || orderDetails.Event_Date)}
                            />
                            <div className="restaurant-detail-field restaurant-detail-field-wide">
                                <span className="restaurant-detail-label">Event location</span>
                                <span className="restaurant-detail-value">
                                    <EventLocationMapLink
                                        address={
                                            orderDetails.eventLocation ||
                                            orderDetails.deliveryAddress ||
                                            orderDetails.Event_Location
                                        }
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="order-details-items-payment-row">
                    <div className="restaurant-detail-card order-details-items-card">
                        <div className="restaurant-detail-card-header">
                            <h3 className="restaurant-detail-section-title">Order Items</h3>
                            <span className="order-details-card-meta">
                                {orderItems.length} {orderItems.length === 1 ? 'item' : 'items'}
                            </span>
                        </div>
                        {orderItems.length > 0 ? (
                            <ul className="order-details-cart-lines">
                                {orderItems.map((item, index) => {
                                    const itemPrice = toMoneyNumber(item.price);
                                    const quantity =
                                        parseInt(item.foodQuantity || item.quantity || 1, 10) || 1;
                                    const lineTotal = itemPrice * quantity;
                                    const itemName =
                                        item.foodItemName ||
                                        item.name ||
                                        item.itemName ||
                                        '—';
                                    return (
                                        <li
                                            key={item.orderLineId || item.foodItemId || index}
                                            className="order-details-cart-line"
                                        >
                                            <div className="order-details-cart-line-info">
                                                <span className="order-details-cart-line-name">
                                                    {itemName}
                                                </span>
                                                <span className="order-details-cart-line-meta">
                                                    {quantity} × {formatMoney(itemPrice)}
                                                </span>
                                            </div>
                                            <span className="order-details-cart-line-total">
                                                {formatMoney(lineTotal)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="order-details-table-empty">
                                No items found for this order.
                            </p>
                        )}
                        {orderItems.length > 0 ? (
                            <div className="order-details-billing">
                                {billingRows.map((row) => (
                                    <div
                                        key={row.label}
                                        className={`order-details-billing-row${
                                            row.emphasis
                                                ? ' order-details-billing-row--total'
                                                : ''
                                        }`}
                                    >
                                        <span>{row.label}</span>
                                        <span>{formatMoney(row.value)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {showPaymentDetails ? (
                        <div className="restaurant-detail-card order-details-payment-card">
                            <div className="restaurant-detail-card-header">
                                <h3 className="restaurant-detail-section-title">
                                    Payment Details
                                </h3>
                            </div>
                            <div className="restaurant-detail-grid order-details-payment-grid">
                                <DetailField
                                    label="Payment method"
                                    value={formatPaymentMethodDisplay(
                                        paymentDetails.paymentMethod || orderDetails
                                    )}
                                />
                                {!isCashPaymentMethod(
                                    paymentDetails.paymentMethod || orderDetails
                                ) ? (
                                    <DetailField
                                        label="Transaction ID"
                                        value={paymentDetails.transactionId}
                                        wide
                                    />
                                ) : null}
                                <DetailField
                                    label="Amount"
                                    value={formatMoney(
                                        paymentDetails.amount || orderDetails.totalPrice
                                    )}
                                />
                                {!isCashPaymentMethod(
                                    paymentDetails.paymentMethod || orderDetails
                                ) ? (
                                    <DetailField
                                        label="Bank RRN"
                                        value={paymentDetails.bankRRN}
                                    />
                                ) : null}
                                <DetailField
                                    label="Customer details"
                                    value={toDisplayLabel(paymentDetails.customerDetails)}
                                    wide
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;
